import React, { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, ShieldAlert, ArrowLeft, Menu, X, User, ChevronDown, LogOut, UserCircle, Mail } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import toast from 'react-hot-toast'

const AdminSignupPage: React.FC = () => {
  const navigate = useNavigate()
  const { register, loading, user, logout } = useAuthStore()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [departments, setDepartments] = useState<Array<{ id: string; name: string }>>([])
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(true)
  
  const getDashboardRoute = () => {
    if (user?.role === 'admin') return '/admin'
    if (user?.role === 'alumni') return '/dashboard'
    if (user?.role === 'employer') return '/employer-dashboard'
    return '/dashboard'
  }

  const handleLogout = () => {
    setIsDropdownOpen(false)
    logout()
  }

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const { data, error } = await supabaseClient
          .from('departments')
          .select('id, name, is_active, display_order')
          .eq('is_active', true)
          .order('display_order', { ascending: true })

        if (error) {
          console.error('Failed to load departments:', error)
          return
        }

        setDepartments((data || []).map(({ id, name }) => ({ id, name })))
      } catch (err) {
        console.error('Unexpected error loading departments:', err)
      } finally {
        setIsLoadingDepartments(false)
      }
    }

    fetchDepartments()
  }, [])
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    adminCode: '',
    department: '',
  })
  
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({})
  const [successMessage, setSuccessMessage] = useState('')
  const [duplicateEmail, setDuplicateEmail] = useState<'confirmed' | 'unconfirmed' | false>(false)
  const [resending, setResending] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [codeInfo, setCodeInfo] = useState<{ admin_type: string; for_department: string | null } | null>(null)

  // Validate admin code in real-time
  const validateAdminCode = async (code: string) => {
    if (!code.trim()) {
      setCodeInfo(null)
      return null
    }

    const { data: codeData, error } = await supabaseClient
      .from('admin_codes')
      .select('code, is_active, expires_at, used_at, admin_type, for_department')
      .ilike('code', code.trim())
      .single()

    if (error || !codeData) {
      setCodeInfo(null)
      return 'Invalid authorization code'
    } else if (!codeData.is_active) {
      setCodeInfo(null)
      return 'This code has been deactivated'
    } else if (codeData.used_at) {
      setCodeInfo(null)
      return 'This code has already been used'
    } else if (new Date(codeData.expires_at) < new Date()) {
      setCodeInfo(null)
      return 'This code has expired'
    }

    // Valid code - store info and lock department if needed
    const codeType = codeData.admin_type || 'department'
    const codeDept = codeData.for_department || null
    
    setCodeInfo({
      admin_type: codeType,
      for_department: codeDept
    })
    
    // If code has a for_department, auto-populate and lock the department field
    if (codeDept) {
      setFormData((prev) => ({ ...prev, department: codeDept }))
    }
    
    return null // No error
  }

  const validateForm = async () => {
    const newErrors: Record<string, string> = {}

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }
    if (!formData.adminCode.trim()) {
      newErrors.adminCode = 'Admin authorization code is required'
    } else {
      // Re-validate code (should already be validated from onBlur, but double-check)
      const codeError = await validateAdminCode(formData.adminCode)
      if (codeError) {
        newErrors.adminCode = codeError
      }
    }
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }
    // Department is only required for non-super admins without a code department
    if (!formData.department.trim() && !codeInfo?.for_department && codeInfo?.admin_type !== 'super_admin') {
      newErrors.department = 'Department is required'
    }
    
    // Verify department matches code if code specifies one (security check)
    if (codeInfo?.for_department && formData.department !== codeInfo.for_department) {
      newErrors.department = 'Department must match admin code'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleResendEmail = async () => {
    if (!successMessage || resending || resendCooldown > 0) return
    setResending(true)
    try {
      const { error } = await supabaseClient.auth.resend({
        type: 'signup',
        email: successMessage,
      })
      if (error) throw error
      toast.success('Verification email resent! Check your inbox.')
      setResendCooldown(60)
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) { clearInterval(timer); return 0 }
          return prev - 1
        })
      }, 1000)
    } catch (err: any) {
      toast.error('Failed to resend: ' + (err.message || 'Unknown error'))
    } finally {
      setResending(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    
    // Clear codeInfo if admin code changes (requires re-validation)
    if (name === 'adminCode') {
      setCodeInfo(null)
    }
    
    // Prevent department changes when locked by code
    if (name === 'department' && codeInfo?.for_department) {
      return // Don't allow department changes when code specifies it
    }
    
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
    if (name === 'email' && duplicateEmail) {
      setDuplicateEmail(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const isValid = await validateForm()
    if (isValid) {
      try {
        // Use 'Records' as default department for super admins if none selected
        const finalDepartment = formData.department || (codeInfo?.admin_type === 'super_admin' ? 'Records' : '')
        
        const success = await register(formData.email, formData.password, {
          first_name: formData.firstName,
          last_name: formData.lastName,
          department: finalDepartment,
          role: 'admin',
          admin_scope: codeInfo?.admin_type === 'super_admin' ? 'super_admin' : 'department',
          admin_code: formData.adminCode.trim()
        })
        
        if (success) {
          // Show email confirmation screen.
          // Code deactivation happens inside create_user_profile() on first login,
          // because there is no session yet until the admin confirms their email.
          setSuccessMessage(formData.email)
        }
      } catch (error: any) {
        if (error.message === 'EMAIL_ALREADY_REGISTERED_CONFIRMED') {
          setDuplicateEmail('confirmed')
        } else if (error.message === 'EMAIL_ALREADY_REGISTERED_UNCONFIRMED') {
          setDuplicateEmail('unconfirmed')
        } else {
          setErrors({ email: error.message || 'Registration failed' })
        }
      }
    }
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundImage: 'url(/uc_nobg.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <Link to="/" className="flex items-center">
              <img src="/trailblaze_logo.png" alt="Trailblaze" className="h-20" />
            </Link>

            <nav className="hidden md:flex space-x-8">
              <Link to="/news" className="text-gray-600 hover:text-gray-900">News & Updates</Link>
              <Link to="/#features" className="text-gray-600 hover:text-gray-900">Features</Link>
              <Link to="/#benefits" className="text-gray-600 hover:text-gray-900">Benefits</Link>
              <Link to="/#about" className="text-gray-600 hover:text-gray-900">About UCLM</Link>
            </nav>

            <div className="hidden md:flex">
              {user ? (
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => navigate(getDashboardRoute())}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                  >
                    Dashboard
                  </button>
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center space-x-2 focus:outline-none"
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center hover:bg-gray-400 transition-colors overflow-hidden">
                        {user?.profile && 'profile_picture' in user.profile && user.profile.profile_picture ? (
                          <img
                            src={user.profile.profile_picture}
                            alt="Profile"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/default-avatar.svg'
                            }}
                          />
                        ) : (
                          <User className="w-6 h-6 text-gray-600" />
                        )}
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                        <div className="px-4 py-2 border-b border-gray-200">
                          <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
                          <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                        </div>
                        <button
                          onClick={() => {
                            setIsDropdownOpen(false)
                            if (user?.role === 'admin') {
                              navigate('/admin/profile')
                            } else if (user?.role === 'alumni') {
                              navigate('/profile')
                            } else if (user?.role === 'employer') {
                              navigate('/employer/profile')
                            }
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <UserCircle className="w-4 h-4" />
                          <span>View Profile</span>
                        </button>
                        <button
                          onClick={handleLogout}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Logout</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <Link to="/login" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
                  Login
                </Link>
              )}
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6 text-gray-600" /> : <Menu className="w-6 h-6 text-gray-600" />}
            </button>
          </div>

          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col space-y-4">
                <Link to="/news" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>News & Updates</Link>
                <Link to="/#features" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>Features</Link>
                <Link to="/#benefits" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>Benefits</Link>
                <Link to="/#about" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>About UCLM</Link>

                <div className="pt-4 border-t border-gray-200">
                  {user ? (
                    <div className="space-y-3">
                      <button
                        onClick={() => {
                          navigate(getDashboardRoute())
                          setIsMobileMenuOpen(false)
                        }}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                      >
                        Dashboard
                      </button>
                      <div className="flex items-center justify-between px-2 py-2 bg-gray-50 rounded">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                            {user?.profile && 'profile_picture' in user.profile && user.profile.profile_picture ? (
                              <img
                                src={user.profile.profile_picture}
                                alt="Profile"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/default-avatar.svg'
                                }}
                              />
                            ) : (
                              <User className="w-4 h-4 text-gray-600" />
                            )}
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-900 truncate">{user?.email}</p>
                            <p className="text-[11px] text-gray-500 capitalize">{user?.role}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (user?.role === 'admin') {
                              navigate('/admin/profile')
                            } else if (user?.role === 'alumni') {
                              navigate('/profile')
                            } else if (user?.role === 'employer') {
                              navigate('/employer/profile')
                            }
                            setIsMobileMenuOpen(false)
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          View
                        </button>
                      </div>
                      <button
                        onClick={() => {
                          handleLogout()
                          setIsMobileMenuOpen(false)
                        }}
                        className="w-full text-gray-700 hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors text-sm flex items-center justify-center space-x-2"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/login"
                      className="block w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-center"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Login
                    </Link>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 sm:p-8">
          <div className="flex items-center justify-center mb-6">
            <ShieldAlert className="w-8 h-8 text-blue-600 mr-2" />
          </div>

          <div className="text-center mb-8">
            <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-2">Trailblaze</div>
            <p className="text-red-600 font-semibold text-sm mb-1">Create Admin Account</p>
            <p className="text-gray-500 text-xs">Authorized personnel registration</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your first name"
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your last name"
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your email"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
              {duplicateEmail && (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                  <p className="font-medium">This email is already registered.</p>
                  {duplicateEmail === 'unconfirmed' ? (
                    <>
                      <p className="mt-1 mb-3">
                        You registered but haven't confirmed your email yet. Resend the verification link below, or{' '}
                        <Link to="/login/admin" className="font-semibold text-blue-600 hover:underline">log in here</Link>.
                      </p>
                      <button
                        type="button"
                        onClick={async () => {
                          if (resending || resendCooldown > 0) return
                          setResending(true)
                          try {
                            const { error } = await supabaseClient.auth.resend({
                              type: 'signup',
                              email: formData.email,
                            })
                            if (error) throw error
                            toast.success('Verification email resent! Check your inbox.')
                            setResendCooldown(60)
                            const timer = setInterval(() => {
                              setResendCooldown((prev) => {
                                if (prev <= 1) { clearInterval(timer); return 0 }
                                return prev - 1
                              })
                            }, 1000)
                          } catch (err: any) {
                            toast.error('Failed to resend: ' + (err.message || 'Unknown error'))
                          } finally {
                            setResending(false)
                          }
                        }}
                        disabled={resending || resendCooldown > 0}
                        className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {resending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Verification Email'}
                      </button>
                    </>
                  ) : (
                    <p className="mt-1">
                      Please use a different email or{' '}
                      <Link to="/login/admin" className="font-semibold text-blue-600 hover:underline">log in here</Link>.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Authorization Code
              </label>
              <input
                type="password"
                name="adminCode"
                value={formData.adminCode}
                onChange={handleChange}
                onBlur={async (e) => {
                  const error = await validateAdminCode(e.target.value)
                  if (error) {
                    setErrors((prev) => ({ ...prev, adminCode: error }))
                  } else {
                    setErrors((prev) => {
                      const { adminCode, ...rest } = prev
                      return rest
                    })
                  }
                }}
                className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.adminCode ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter admin code"
              />
              {errors.adminCode && (
                <p className="mt-1 text-sm text-red-600">{errors.adminCode}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">Contact an existing administrator for the authorization code</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                College
              </label>
              {codeInfo?.for_department ? (
                <>
                  <input
                    type="text"
                    value={formData.department}
                    disabled
                    className="w-full px-4 py-2 border rounded-lg bg-gray-50 text-gray-600 border-gray-300 cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-green-600">Department assigned by admin code (cannot be changed)</p>
                </>
              ) : codeInfo?.admin_type === 'super_admin' ? (
                <>
                  <input
                    type="text"
                    value="Records"
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-blue-600">Super Admin accounts are assigned to the Records office</p>
                </>
              ) : (
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  disabled={isLoadingDepartments}
                  className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.department ? 'border-red-500' : 'border-gray-300'
                  } ${isLoadingDepartments ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
                >
                  <option value="">
                    {isLoadingDepartments ? 'Loading colleges...' : 'Select college'}
                  </option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.name}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              )}
              {errors.department && (
                <p className="mt-1 text-sm text-red-600">{errors.department}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Create a strong password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            {!successMessage && (
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white font-semibold py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Admin Account...' : 'Create Admin Account'}
              </button>
            )}
          </form>

          {successMessage && (
            <div className="text-center space-y-4 py-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <Mail className="w-9 h-9 text-green-600" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Check Your Email</h2>
              <p className="text-sm text-gray-600">
                We sent a verification link to <span className="font-semibold text-gray-800">{successMessage}</span>.
                Click the link in the email to confirm your account.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">What happens next?</h3>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Verify your email by clicking the link we sent</li>
                  <li>Log in with your admin credentials</li>
                  <li>You'll have full access to the admin dashboard</li>
                </ol>
              </div>
              <button
                onClick={handleResendEmail}
                disabled={resending || resendCooldown > 0}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {resending
                  ? 'Sending...'
                  : resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : "Didn't receive it? Resend email"}
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/login/admin" className="text-sm text-blue-600 hover:underline">
              Back to Login
            </Link>
          </div>
        </div>

        <div className="mt-6 flex justify-center">
          <Link
            to="/"
            className="flex items-center gap-2 text-xs sm:text-sm text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
            Back to Home
          </Link>
        </div>
      </div>
      </div>
    </div>
  )
}

export default AdminSignupPage
